import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  TrendingUp, TrendingDown, Package, FileText, Users, 
  AlertTriangle, Clock, CheckCircle2, Euro, Building2,
  ArrowUpRight, ArrowDownRight, Calendar, Search,
  RefreshCw, Award, LayoutGrid, LayoutList, MoreHorizontal, 
  ChevronRight, Truck, MapPin, ShoppingCart, Plus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Area, AreaChart } from "recharts";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RicambistaDashboard({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [officineStats, setOfficineStats] = useState([]);
  const [pointsOverview, setPointsOverview] = useState(null);
  const [topOfficine, setTopOfficine] = useState([]);
  const [urgentOrders, setUrgentOrders] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [workshopActivities, setWorkshopActivities] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [revenuePeriod, setRevenuePeriod] = useState('year');
  
  // View mode: 'classic' or 'grid'
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('ricambistaDashboardView') || 'classic';
  });

  useEffect(() => {
    localStorage.setItem('ricambistaDashboardView', viewMode);
  }, [viewMode]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    updateRevenueData();
  }, [revenuePeriod]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, requestsRes, ordersRes, officineRes, pointsRes] = await Promise.all([
        authFetch(`${API}/ricambista/stats`, { }),
        authFetch(`${API}/ricambista/requests/recent`, { }),
        authFetch(`${API}/ricambista/orders/recent`, { }),
        authFetch(`${API}/ricambista/officine/stats`, { }),
        authFetch(`${API}/ricambista/points/overview`, { })
      ]);

      let statsData = null;
      if (statsRes.ok) {
        statsData = await statsRes.json();
        setStats(statsData);
        setTopOfficine(statsData?.top_officine || []);
        setUrgentOrders(statsData?.urgent_orders || []);
        setLowStockItems(statsData?.low_stock_items || []);
        setWorkshopActivities(statsData?.workshop_activities || []);
      }
      if (requestsRes.ok) setRecentRequests(await requestsRes.json());
      if (ordersRes.ok) setRecentOrders(await ordersRes.json());
      if (officineRes.ok) setOfficineStats(await officineRes.json());
      if (pointsRes.ok) setPointsOverview(await pointsRes.json());
      
      // Revenue data
      updateRevenueData();
      
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const updateRevenueData = () => {
    // Revenue data will come from the API stats
    // Only set empty data if no real data is available
    setRevenueData([]);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };
  
  // Calculate totals
  const chartTotal = revenueData.reduce((sum, item) => sum + (item.value || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-zinc-400 text-sm mt-1">
            Dashboard del ricambista che ha collegate le officine
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Action Buttons */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/ricambista/richieste')}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <Plus className="w-4 h-4 mr-1" />
            Nuovo preventivo
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/ricambista/ordini')}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <Plus className="w-4 h-4 mr-1" />
            Nuovo ordine
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <Search className="w-4 h-4 mr-1" />
            Cerca ricambio
          </Button>
          
          {/* View Toggle */}
          <div className="flex items-center bg-zinc-800 rounded-lg p-1 ml-2">
            <button
              onClick={() => setViewMode('classic')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'classic' 
                  ? 'bg-primary text-black' 
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="view-classic-btn"
            >
              <LayoutList className="w-4 h-4" />
              Classica
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-primary text-black' 
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="view-grid-btn"
            >
              <LayoutGrid className="w-4 h-4" />
              Griglia
            </button>
          </div>
        </div>
      </div>

      {/* ============== CLASSIC VIEW ============== */}
      {viewMode === 'classic' && (
        <>
          {/* KPI Cards Row - 5 Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Venduto oggi - RED/ORANGE gradient */}
            <div 
              className="rounded-xl p-4 cursor-pointer hover:opacity-90 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #dc2626 0%, #ea580c 100%)' }}
              onClick={() => navigate('/ricambista/ordini')}
            >
              <p className="text-3xl font-bold text-white">€ {(stats?.revenue_today || 0).toLocaleString('it-IT')}</p>
              <p className="text-white/80 text-sm mt-1">Venduto oggi</p>
              <div className="flex items-center gap-1 mt-2 text-white/70 text-xs">
                <ArrowUpRight className="w-3 h-3" />
                <span>+14</span>
                <span className="ml-1">18% rispetto a ieri</span>
              </div>
            </div>
            
            {/* Ordini oggi - ORANGE */}
            <div 
              className="rounded-xl p-4 cursor-pointer hover:opacity-90 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}
              onClick={() => navigate('/ricambista/ordini')}
            >
              <p className="text-3xl font-bold text-white">€ {stats?.orders_today || 0}</p>
              <p className="text-white/80 text-sm mt-1">Ordini oggi</p>
              <div className="flex items-center justify-between mt-2 text-white/70 text-xs">
                <span>Evasi: {stats?.orders_completed || 0}</span>
                <span>In preparazione</span>
              </div>
            </div>
            
            {/* Preventivi aperti - TEAL */}
            <div 
              className="rounded-xl p-4 cursor-pointer hover:opacity-90 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)' }}
              onClick={() => navigate('/ricambista/richieste')}
            >
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-white/80" />
                <p className="text-3xl font-bold text-white">{stats?.pending_requests || 0}</p>
              </div>
              <p className="text-white/80 text-sm mt-1">Preventivi aperti</p>
              <p className="text-white/60 text-xs mt-2">In attesa di risposta</p>
            </div>
            
            {/* Officine attive - GREEN */}
            <div 
              className="rounded-xl p-4 cursor-pointer hover:opacity-90 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}
              onClick={() => navigate('/ricambista/officine')}
            >
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-white/80" />
                <p className="text-3xl font-bold text-white">{stats?.active_officine || 0}</p>
              </div>
              <p className="text-white/80 text-sm mt-1">Officine attive</p>
              <div className="flex items-center gap-2 mt-2 text-white/70 text-xs">
                <span>Collegate alla tua rete</span>
              </div>
            </div>
            
            {/* Top 3 - DARK BLUE/GRAY */}
            <div 
              className="rounded-xl p-4 cursor-pointer hover:opacity-90 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #3f3f46 0%, #27272a 100%)' }}
              onClick={() => navigate('/ricambista/officine')}
            >
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-white/80" />
                <p className="text-3xl font-bold text-white">{topOfficine.length}</p>
              </div>
              <p className="text-white/80 text-sm mt-1">{topOfficine.length > 0 ? 'Top officine' : 'Nessuna officina'}</p>
              <div className="flex items-center gap-1 mt-2 text-white/70 text-xs">
                <ChevronRight className="w-3 h-3" />
                <span>{topOfficine[0]?.name || 'N/D'}</span>
              </div>
            </div>
          </div>

          {/* Main Content - 2 Columns */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* LEFT - 2/3 */}
            <div className="lg:col-span-2 space-y-5">
              {/* Statistiche fatturato */}
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-medium text-white">Statistiche fatturato</h3>
                  <div className="flex items-center gap-2 text-sm">
                    <button 
                      onClick={() => setRevenuePeriod('week')}
                      className={`px-3 py-1 rounded transition-colors ${
                        revenuePeriod === 'week' ? 'bg-primary/20 text-primary' : 'text-zinc-500 hover:text-zinc-300'
                      }`}
                    >
                      Ultimi 7 giorni
                    </button>
                    <button 
                      onClick={() => setRevenuePeriod('month')}
                      className={`px-3 py-1 rounded transition-colors ${
                        revenuePeriod === 'month' ? 'bg-primary/20 text-primary' : 'text-zinc-500 hover:text-zinc-300'
                      }`}
                    >
                      Questo mese
                    </button>
                  </div>
                </div>
                
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="text-4xl font-bold text-white">€{chartTotal.toLocaleString('it-IT')}</span>
                  <span className="text-zinc-400 text-sm">
                    {revenuePeriod === 'year' ? "fatturato quest'anno" : 
                     revenuePeriod === 'month' ? "fatturato questo mese" : "fatturato ultimi 7 giorni"}
                  </span>
                </div>
                <p className="text-primary text-sm mb-4">+22% rispetto all'anno scorso</p>
                
                {/* Chart */}
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={revenueData}>
                      <XAxis 
                        dataKey="label" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: '#71717a', fontSize: 11 }}
                      />
                      <YAxis hide />
                      <Tooltip 
                        contentStyle={{ 
                          background: '#18181b', 
                          border: '1px solid #3f3f46',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`€${value.toLocaleString('it-IT')}`, 'Fatturato']}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#f97316" 
                        strokeWidth={2}
                        dot={{ fill: '#f97316', strokeWidth: 0, r: 4 }}
                        activeDot={{ fill: '#f97316', strokeWidth: 0, r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Bottom Row - 3 columns */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {/* Ordini urgenti */}
                <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-medium text-white">Ordini urgenti</h3>
                    <button className="text-zinc-500 hover:text-white">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {urgentOrders.map((order, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between py-1.5 hover:bg-zinc-800/30 rounded px-1 -mx-1 cursor-pointer transition-colors"
                        onClick={() => navigate('/ricambista/ordini')}
                      >
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-zinc-500" />
                          <span className="text-white text-sm truncate max-w-[100px]">{order.officina}</span>
                        </div>
                        <div className="flex items-center gap-1 text-zinc-400 text-xs">
                          <span>{order.item}</span>
                          <ChevronRight className="w-3 h-3" />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    onClick={() => navigate('/ricambista/ordini')}
                    className="text-primary text-sm mt-3 hover:underline"
                  >
                    Visualizza tutte
                  </button>
                </div>

                {/* Articoli in esaurimento */}
                <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-medium text-white">Articoli in esaurimento</h3>
                    <button className="text-zinc-500 hover:text-white">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {lowStockItems.map((item, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between py-1.5 hover:bg-zinc-800/30 rounded px-1 -mx-1 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <AlertTriangle className={`w-4 h-4 ${item.quantity === 0 ? 'text-red-500' : 'text-yellow-500'}`} />
                          <span className="text-white text-sm">{item.name}</span>
                          <span className="text-zinc-500 text-xs">({item.quantity})</span>
                        </div>
                        <button className="text-zinc-500 hover:text-white">
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    onClick={() => navigate('/ricambista/catalogo')}
                    className="text-primary text-sm mt-3 hover:underline"
                  >
                    Visualizza tutte
                  </button>
                </div>

                {/* Attività officine - Map */}
                <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-medium text-white">Attività officine</h3>
                    <button className="text-zinc-500 hover:text-white">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Simple map placeholder with markers */}
                  <div className="relative h-32 bg-zinc-800 rounded-lg overflow-hidden">
                    <div className="absolute inset-0 opacity-30" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                    
                    {/* Map markers */}
                    <div className="absolute top-4 left-8 w-6 h-6 bg-primary rounded-full flex items-center justify-center text-black font-bold text-xs shadow-lg">
                      1
                    </div>
                    <div className="absolute bottom-6 left-1/2 w-6 h-6 bg-primary rounded-full flex items-center justify-center text-black font-bold text-xs shadow-lg">
                      2
                    </div>
                    <div className="absolute top-6 right-6 w-6 h-6 bg-primary rounded-full flex items-center justify-center text-black font-bold text-xs shadow-lg">
                      3
                    </div>
                  </div>
                </div>
              </div>

              {/* Attività officine - List */}
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-medium text-white">Attività officine</h3>
                  <button className="text-zinc-500 hover:text-white">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="space-y-2">
                  {workshopActivities.map((activity, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between py-2 hover:bg-zinc-800/30 rounded px-2 -mx-2 cursor-pointer transition-colors"
                      onClick={() => navigate('/ricambista/officine')}
                    >
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-zinc-500" />
                        <span className="text-white font-medium text-sm">{activity.officina}</span>
                        <span className="text-zinc-400 text-sm">{activity.action}</span>
                      </div>
                      <span className="text-zinc-500 text-sm font-mono">{activity.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT - 1/3 */}
            <div className="space-y-5">
              {/* Top officine */}
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-medium text-white">Top officine</h3>
                  <button className="text-zinc-500 hover:text-white">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="space-y-3">
                  {topOfficine.map((officina, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between py-2 hover:bg-zinc-800/30 rounded px-2 -mx-2 cursor-pointer transition-colors"
                      onClick={() => navigate('/ricambista/officine')}
                    >
                      <div className="flex items-center gap-2">
                        {/* Italian flag */}
                        <span className="inline-block w-5 h-3" style={{
                          background: 'linear-gradient(to right, #009246 33%, #fff 33%, #fff 66%, #ce2b37 66%)'
                        }}></span>
                        <span className="text-white text-sm">{officina.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-white font-mono text-sm">€ {officina.revenue.toLocaleString('it-IT')}</span>
                        <ChevronRight className="w-4 h-4 text-zinc-500" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Duplicate sections for right column as shown in image */}
              {/* Ordini urgenti */}
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-medium text-white">Ordini urgenti</h3>
                  <button className="text-zinc-500 hover:text-white">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="space-y-2">
                  {urgentOrders.map((order, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between py-1.5"
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-zinc-500" />
                        <span className="text-white text-sm">{order.officina}</span>
                      </div>
                      <span className="text-zinc-400 text-xs">{order.item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Articoli in esaurimento */}
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-medium text-white">Articoli in esaurimento</h3>
                  <button className="text-zinc-500 hover:text-white">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="space-y-2">
                  {lowStockItems.map((item, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between py-1.5"
                    >
                      <div className="flex items-center gap-2">
                        <AlertTriangle className={`w-4 h-4 ${item.quantity === 0 ? 'text-red-500' : 'text-yellow-500'}`} />
                        <span className="text-white text-sm">{item.name}</span>
                        <span className="text-zinc-500 text-xs">({item.quantity})</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-zinc-500" />
                    </div>
                  ))}
                </div>
                
                <button 
                  onClick={() => navigate('/ricambista/catalogo')}
                  className="text-primary text-sm mt-3 hover:underline"
                >
                  Visualizza tutte
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ============== GRID VIEW ============== */}
      {viewMode === 'grid' && (
        <>
          {/* Large Colored Grid Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {/* Fatturato Mese - GREEN */}
            <div 
              className="rounded-2xl p-6 cursor-pointer hover:scale-[1.02] transition-transform"
              style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}
              onClick={() => navigate('/ricambista/ordini')}
            >
              <Euro className="w-8 h-8 text-white/80 mb-3" />
              <p className="text-4xl font-bold text-white">{formatCurrency(chartTotal)}</p>
              <p className="text-white/80 text-sm mt-2">Fatturato Anno</p>
              <div className="flex items-center gap-1 mt-2 text-white/80 text-xs">
                <ArrowUpRight className="w-3 h-3" />
                <span>+22% vs anno precedente</span>
              </div>
            </div>
            
            {/* Ordini Oggi - BLUE */}
            <div 
              className="rounded-2xl p-6 cursor-pointer hover:scale-[1.02] transition-transform"
              style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}
              onClick={() => navigate('/ricambista/ordini')}
            >
              <Package className="w-8 h-8 text-white/80 mb-3" />
              <p className="text-4xl font-bold text-white">€ {stats?.orders_today || 0}</p>
              <p className="text-white/80 text-sm mt-2">Ordini Oggi</p>
              <p className="text-white/60 text-xs mt-2">Evasi: {stats?.orders_completed || 0}</p>
            </div>
            
            {/* Preventivi aperti - ORANGE */}
            <div 
              className="rounded-2xl p-6 cursor-pointer hover:scale-[1.02] transition-transform"
              style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}
              onClick={() => navigate('/ricambista/richieste')}
            >
              <FileText className="w-8 h-8 text-white/80 mb-3" />
              <p className="text-4xl font-bold text-white">{stats?.pending_requests || 0}</p>
              <p className="text-white/80 text-sm mt-2">Preventivi Aperti</p>
              <p className="text-white/60 text-xs mt-2">Da gestire oggi</p>
            </div>
            
            {/* Officine Attive - PURPLE */}
            <div 
              className="rounded-2xl p-6 cursor-pointer hover:scale-[1.02] transition-transform"
              style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}
              onClick={() => navigate('/ricambista/officine')}
            >
              <Users className="w-8 h-8 text-white/80 mb-3" />
              <p className="text-4xl font-bold text-white">{stats?.active_officine || 0}</p>
              <p className="text-white/80 text-sm mt-2">Officine Attive</p>
              <p className="text-white/60 text-xs mt-2">negli ultimi 30 giorni</p>
            </div>
            
            {/* Venduto Oggi - RED */}
            <div 
              className="rounded-2xl p-6 cursor-pointer hover:scale-[1.02] transition-transform"
              style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}
              onClick={() => navigate('/ricambista/ordini')}
            >
              <TrendingUp className="w-8 h-8 text-white/80 mb-3" />
              <p className="text-4xl font-bold text-white">€ {(stats?.revenue_today || 0).toLocaleString('it-IT')}</p>
              <p className="text-white/80 text-sm mt-2">Venduto Oggi</p>
              <p className="text-white/60 text-xs mt-2">+18% vs ieri</p>
            </div>
            
            {/* Articoli in esaurimento - TEAL */}
            <div 
              className="rounded-2xl p-6 cursor-pointer hover:scale-[1.02] transition-transform"
              style={{ background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)' }}
            >
              <AlertTriangle className="w-8 h-8 text-white/80 mb-3" />
              <p className="text-4xl font-bold text-white">{lowStockItems.length}</p>
              <p className="text-white/80 text-sm mt-2">Articoli in Esaurimento</p>
              <p className="text-white/60 text-xs mt-2">Da riordinare</p>
            </div>
            
            {/* Punti Network - AMBER */}
            <div 
              className="rounded-2xl p-6 cursor-pointer hover:scale-[1.02] transition-transform"
              style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
            >
              <Award className="w-8 h-8 text-white/80 mb-3" />
              <p className="text-4xl font-bold text-white">{pointsOverview?.total_network_points || 0}</p>
              <p className="text-white/80 text-sm mt-2">Punti Network</p>
              <p className="text-white/60 text-xs mt-2">totale officine</p>
            </div>
            
            {/* Top 3 - GRAY */}
            <div 
              className="rounded-2xl p-6 cursor-pointer hover:scale-[1.02] transition-transform"
              style={{ background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)' }}
              onClick={() => navigate('/ricambista/officine')}
            >
              <Award className="w-8 h-8 text-white/80 mb-3" />
              <p className="text-4xl font-bold text-white">{topOfficine.length}</p>
              <p className="text-white/80 text-sm mt-2">Top Officine</p>
              <p className="text-white/60 text-xs mt-2">{topOfficine.length > 0 ? 'Per fatturato' : 'Nessuna officina'}</p>
            </div>
          </div>

          {/* Lists */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Top Officine */}
            <div className="card">
              <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                <h3 className="font-semibold text-white">Top Officine</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/ricambista/officine')}>
                  Vedi tutte
                </Button>
              </div>
              <div className="divide-y divide-zinc-800">
                {topOfficine.map((officina, index) => (
                  <div key={index} className="p-3 hover:bg-zinc-800/50 transition-colors flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-5 h-3" style={{
                        background: 'linear-gradient(to right, #009246 33%, #fff 33%, #fff 66%, #ce2b37 66%)'
                      }}></span>
                      <span className="text-white">{officina.name}</span>
                    </div>
                    <span className="text-green-400 font-semibold">€ {officina.revenue.toLocaleString('it-IT')}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Ordini Urgenti */}
            <div className="card">
              <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                <h3 className="font-semibold text-white">Ordini Urgenti</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/ricambista/ordini')}>
                  Vedi tutti
                </Button>
              </div>
              <div className="divide-y divide-zinc-800">
                {urgentOrders.map((order, index) => (
                  <div key={index} className="p-3 hover:bg-zinc-800/50 transition-colors flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-zinc-500" />
                      <span className="text-white">{order.officina}</span>
                    </div>
                    <span className="text-zinc-400 text-sm">{order.item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
